import { Alert, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertOctagon, IconCheck } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as commonClasses from '@/styles/componentCommons.css'
import { ERROR_MESSAGES, RESEND_COOLDOWN_TIME, VERIFICATION_CODE_LENGTH } from '@/utils/onboardingConstants'
import { handleOnboardingError, showOnboardingErrorNotification } from '@/utils/onboardingErrorHandling'

const EmailVerificationSchema = z.object({
  code: z.string().length(VERIFICATION_CODE_LENGTH, ERROR_MESSAGES.VERIFICATION_CODE_LENGTH),
})

interface EmailVerificationFormProps {
  email: string
  customerInfoId: string
  onSuccess: () => void
  onResendCode: () => void
}

export function EmailVerificationForm({ email, customerInfoId, onSuccess, onResendCode }: EmailVerificationFormProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [cooldownTime, setCooldownTime] = useState(0)

  // Individual code input refs for auto-advance functionality
  const codeInputRefs = useRef<Array<HTMLInputElement | null>>(Array(VERIFICATION_CODE_LENGTH).fill(null))
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(VERIFICATION_CODE_LENGTH).fill(''))

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime(time => Math.max(0, time - 1))
      }, 1000)
    }

    return () => clearInterval(timer)
  }, [cooldownTime])

  const form = useForm<z.infer<typeof EmailVerificationSchema>>({
    mode: 'controlled',
    validate: zodResolver(EmailVerificationSchema),
    initialValues: {
      code: '',
    },
  })

  // Handle code input changes with auto-advance
  const handleCodeDigitChange = (value: string, index: number) => {
    // Handle paste events (multiple digits)
    if (value.length > 1) {
      const digits = value.replace(/[^\d]/g, '').split('').slice(0, VERIFICATION_CODE_LENGTH)
      const newDigits = [...codeDigits]

      digits.forEach((digit, i) => {
        if (index + i < VERIFICATION_CODE_LENGTH) {
          newDigits[index + i] = digit
        }
      })

      setCodeDigits(newDigits)
      form.setFieldValue('code', newDigits.join(''))

      // Focus on the next empty field or last field
      const nextIndex = Math.min(index + digits.length, VERIFICATION_CODE_LENGTH - 1)
      codeInputRefs.current[nextIndex]?.focus()

      return
    }

    // Handle single digit input
    if (value && !/^\d$/.test(value)) return

    const newDigits = [...codeDigits]
    newDigits[index] = value
    setCodeDigits(newDigits)
    form.setFieldValue('code', newDigits.join(''))

    // Auto-advance to next field
    if (value && index < VERIFICATION_CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus()
    }
  }

  const handleCodeKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Backspace') {
      const newDigits = [...codeDigits]

      // If current field is empty and we're not at the first input, move back
      if (!codeDigits[index] && index > 0) {
        codeInputRefs.current[index - 1]?.focus()
        newDigits[index - 1] = ''
      } else {
        // If current field has a value, just clear it
        newDigits[index] = ''
      }

      setCodeDigits(newDigits)
      form.setFieldValue('code', newDigits.join(''))
    }
  }

  const verifyEmailMutation = useMutation({
    mutationFn: (code: string) => api.verifyEmail({ code, customer_info_uuid: customerInfoId }),
    onSuccess: () => {
      notifications.show({
        title: 'Email Verified!',
        message: 'Your email address has been successfully verified.',
        color: 'green',
        icon: <IconCheck size={18} />,
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      onSuccess()
    },
    onError: (error: any) => {
      const onboardingError = handleOnboardingError(error, 'verify-email')
      setSubmitError(onboardingError.message)
    },
  })

  const resendCodeMutation = useMutation({
    mutationFn: () =>
      api.sendConfirmationEmail({
        customer_info_uuid: customerInfoId,
      }),
    onSuccess: () => {
      setCooldownTime(RESEND_COOLDOWN_TIME)
      setCodeDigits(Array(VERIFICATION_CODE_LENGTH).fill(''))
      form.setFieldValue('code', '')
      setSubmitError(null)
      notifications.show({
        title: 'Code Resent',
        message: 'A new verification code has been sent to your email.',
        color: 'blue',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      onResendCode()
    },
    onError: (error: any) => {
      const onboardingError = handleOnboardingError(error, 'resend-code')
      showOnboardingErrorNotification(onboardingError, 'resending verification code')
    },
  })

  const handleSubmit = useCallback(
    (values: z.infer<typeof EmailVerificationSchema>) => {
      setSubmitError(null)
      verifyEmailMutation.mutate(values.code)
    },
    [verifyEmailMutation],
  )

  const handleResendCode = useCallback(() => {
    if (cooldownTime > 0 || isResending) return
    setIsResending(true)
    resendCodeMutation.mutate()
    setIsResending(false)
  }, [cooldownTime, isResending, resendCodeMutation])

  const canSubmit = () => {
    const { code } = form.getValues()
    return !verifyEmailMutation.isPending && code.length === VERIFICATION_CODE_LENGTH
  }

  return (
    <Stack gap="md" mt={isMobile ? 'sm' : 'xl'}>
      <Title order={3} ta="center">
        {isMobile ? (
          <>
            Validate Your
            <br />
            Email Address
          </>
        ) : (
          'Validate Your Email Address'
        )}
      </Title>
      <Text c="dimmed">Enter the six-digit code we sent to your email.</Text>

      <Text ta="center" c="dimmed" size="sm">
        We sent a code to <strong>{email}</strong>
      </Text>

      {submitError && <Alert variant="light" color="red" title={submitError} icon={<IconAlertOctagon />} />}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          {/* Verification Code Inputs */}
          <Stack gap="xs">
            <Group gap="xs" justify="center">
              {Array.from({ length: VERIFICATION_CODE_LENGTH }).map((_, index) => (
                <TextInput
                  key={index}
                  ref={el => {
                    codeInputRefs.current[index] = el
                  }}
                  size={isMobile ? 'md' : 'lg'}
                  w={isMobile ? 40 : 50}
                  maw={isMobile ? 40 : 50}
                  ta="center"
                  maxLength={VERIFICATION_CODE_LENGTH}
                  value={codeDigits[index]}
                  onChange={e => handleCodeDigitChange(e.target.value, index)}
                  onKeyDown={e => handleCodeKeyDown(e, index)}
                  error={form.errors.code && index === 0}
                  autoFocus={index === 0}
                  styles={{
                    input: {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 600,
                    },
                  }}
                />
              ))}
            </Group>
            {form.errors.code && (
              <Text size="xs" c="red" ta="center">
                {form.errors.code}
              </Text>
            )}
          </Stack>

          <Group justify="center" gap="xs">
            <Text size="sm" c="dimmed">
              Didn&apos;t receive an email? Check your junk folder.
            </Text>
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={handleResendCode}
              disabled={cooldownTime > 0 || resendCodeMutation.isPending}
              loading={resendCodeMutation.isPending}>
              {cooldownTime > 0 ? `Resend in ${cooldownTime}s` : 'Resend code'}
            </Button>
          </Group>

          <Group justify="flex-end" mt="xl">
            <Button type="submit" disabled={!canSubmit()} loading={verifyEmailMutation.isPending}>
              {verifyEmailMutation.isPending ? 'Verifying...' : 'Verify Email'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  )
}
